// ─── Firebase (Core) ──────────────────────────────────────────────
// PROJET PARTAGÉ : toutes les apps de la suite pointent sur la MÊME base → un
// seul compte, une seule donnée. Historiquement « mopcal-96460 », hérité de
// MoPCal — d'où le nom qui ne colle plus à MoP Suite.
// La configuration (et donc le PROJET VISÉ) vit désormais dans
// `firebase.config.js` : un seul point à changer pour basculer. Voir
// `BASCULE-PROJET-FIREBASE.md`.
import { initializeApp, getApps } from 'firebase/app';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  getFirestore,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import {
  initializeAuth, getAuth, getReactNativePersistence, browserLocalPersistence,
} from 'firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

import { connectFirestoreEmulator } from 'firebase/firestore';
import { connectAuthEmulator } from 'firebase/auth';
import { connectStorageEmulator } from 'firebase/storage';

import {
  firebaseConfig, DB_APP, REGION_FONCTIONS, PROJET_PAR_DEFAUT, projetSurcharge,
  EMULATEURS, EMU_HOTE, EMU_PORTS,
} from './firebase.config';

export { firebaseConfig };
export const FIREBASE_API_KEY = firebaseConfig.apiKey;

// Garde-fou : viser une autre base ne doit JAMAIS être silencieux — sinon on
// croit travailler sur la prod alors qu'on est ailleurs, ou l'inverse.
if (projetSurcharge) {
  console.warn(
    `[MoP Suite] Firebase : projet « ${firebaseConfig.projectId} » `
    + `(et non « ${PROJET_PAR_DEFAUT} ») — base ${DB_APP}, fonctions ${REGION_FONCTIONS}.`,
  );
}

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

function creerFirestore() {
  const commun = { experimentalAutoDetectLongPolling: true };
  // En émulateur, PAS de cache persistant : l'IndexedDB est indexée par projet,
  // et une base locale qu'on remet à zéro entre deux essais laisserait sinon des
  // documents fantômes — on croirait tester un semis propre en relisant l'ancien.
  if (EMULATEURS) {
    try { return initializeFirestore(app, commun); } catch { return getFirestore(app); }
  }
  try {
    if (Platform.OS === 'web') {
      return initializeFirestore(app, {
        ...commun,
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      });
    }
    return initializeFirestore(app, commun);
  } catch {
    return getFirestore(app);
  }
}

function creerAuth() {
  try {
    return initializeAuth(app, {
      persistence: Platform.OS === 'web'
        ? browserLocalPersistence
        : getReactNativePersistence(AsyncStorage),
    });
  } catch {
    return getAuth(app);
  }
}

export const db        = creerFirestore();
export const storage   = getStorage(app);
export const auth      = creerAuth();
export const dbUser    = db;
// Base « application / administration » (config, i18n admin, IVV, forfaits…).
export const dbApp     = getFirestore(app, DB_APP);
export const functions = getFunctions(app, REGION_FONCTIONS);

// ── Bascule sur les émulateurs ────────────────────────────────────
// Doit venir APRÈS la création des services et AVANT toute lecture/écriture :
// `connect*Emulator` refuse un service déjà utilisé.
// Les DEUX bases Firestore sont redirigées — `dbApp` porte la config, les
// forfaits et l'IVV : l'oublier donnerait une app à moitié en local, la moitié
// la plus trompeuse (écrans vides sans erreur).
if (EMULATEURS) {
  // Impossible de rater qu'on n'est PAS sur la vraie base. Le même raisonnement
  // que le garde-fou de projet ci-dessus, en plus fort : ici on ÉCRIT en local.
  console.warn(
    `[MoP Suite] ÉMULATEURS LOCAUX (${EMU_HOTE}) — auth ${EMU_PORTS.auth}, `
    + `firestore ${EMU_PORTS.firestore}, storage ${EMU_PORTS.storage}. `
    + 'Aucune donnée réelle n\'est lue ni écrite.',
  );
  try {
    // `disableWarnings` : sans ça le SDK loggue un avertissement par appel, il
    // noie la console au point de masquer les vraies erreurs qu'on teste.
    connectAuthEmulator(auth, `http://${EMU_HOTE}:${EMU_PORTS.auth}`, { disableWarnings: true });
    connectFirestoreEmulator(db,    EMU_HOTE, EMU_PORTS.firestore);
    connectFirestoreEmulator(dbApp, EMU_HOTE, EMU_PORTS.firestore);
    connectStorageEmulator(storage, EMU_HOTE, EMU_PORTS.storage);
  } catch (e) {
    console.error('[MoP Suite] Branchement émulateur impossible :', e?.message || e);
  }
}
// Les Cloud Functions ne sont VOLONTAIREMENT pas émulées : leur émulateur exige
// `cd functions && npm install` (dépendances lourdes, dont @google-cloud/vision).
// Conséquence à connaître : les callables (`appliquerClaims`, jetons…) échouent
// en mode émulateur. Les claims de test se posent donc directement par l'API
// REST de l'émulateur Auth — c'est ce que fait `scripts/semer-emulateur.js`.
